import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useCatalogStore } from '../../stores/catalogStore'
import CatalogNav       from '../../components/catalog/CatalogNav'
import CatalogSearch    from '../../components/catalog/CatalogSearch'
import EntryTypeFilter  from '../../components/catalog/EntryTypeFilter'
import FacetPanel       from '../../components/catalog/FacetPanel'
import CatalogEntryCard from '../../components/catalog/CatalogEntryCard'

// ---- URL ↔ store serialization -----------------------------------------
// dimension filters → dimension[slug]=a,b
// entry_type, search, sort, page, per_page → flat params
function readFiltersFromUrl(searchParams) {
  const filters = {
    dimensions: {},
    entry_type: null,
    search: '',
    initiative_id: null,
    person_id: null,
  }

  for (const [ key, value ] of searchParams.entries()) {
    const dimMatch = key.match(/^dimension\[(.+)\]$/)
    if (dimMatch) {
      filters.dimensions[dimMatch[1]] = value.split(',').filter(Boolean)
      continue
    }
    if (key === 'entry_type')    filters.entry_type    = value || null
    if (key === 'search')        filters.search        = value || ''
    if (key === 'initiative_id') filters.initiative_id = value || null
    if (key === 'person_id')     filters.person_id     = value || null
  }

  const sort     = searchParams.get('sort') || 'created_at'
  const status   = searchParams.get('status') || 'published'
  const page     = parseInt(searchParams.get('page'),     10) || 1
  const per_page = parseInt(searchParams.get('per_page'), 10) || 25

  return { filters, sort, status, page, per_page }
}

function writeFiltersToUrl({ filters, sort, status, page, per_page }) {
  const params = new URLSearchParams()
  Object.entries(filters.dimensions || {}).forEach(([ dim, values ]) => {
    if (values && values.length > 0) {
      params.set(`dimension[${dim}]`, values.join(','))
    }
  })
  if (filters.entry_type)    params.set('entry_type',    filters.entry_type)
  if (filters.search)        params.set('search',        filters.search)
  if (filters.initiative_id) params.set('initiative_id', filters.initiative_id)
  if (filters.person_id)     params.set('person_id',     filters.person_id)
  if (sort && sort !== 'created_at')  params.set('sort', sort)
  if (status && status !== 'published') params.set('status', status)
  if (page && page !== 1)            params.set('page', page)
  if (per_page && per_page !== 25)   params.set('per_page', per_page)
  return params
}

// ---- State sub-components ----------------------------------------------
function SkeletonGrid({ count = 6 }) {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="rounded-xl border border-slate-200 bg-white p-5 h-52 animate-pulse">
          <div className="h-4 w-3/4 bg-slate-200 rounded mb-3"></div>
          <div className="h-3 w-1/2 bg-slate-200 rounded mb-6"></div>
          <div className="h-3 w-full bg-slate-100 rounded mb-2"></div>
          <div className="h-3 w-full bg-slate-100 rounded mb-2"></div>
          <div className="h-3 w-5/6 bg-slate-100 rounded"></div>
        </div>
      ))}
    </div>
  )
}

function FilteredEmptyState({ onClear }) {
  return (
    <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center">
      <p className="text-sm text-slate-500">No entries match your filters.</p>
      <button
        type="button"
        onClick={onClear}
        className="mt-4 rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
      >
        Clear filters
      </button>
    </div>
  )
}

function FirstRunEmptyState() {
  return (
    <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center">
      <p className="text-sm font-medium text-slate-700">
        No knowledge has been cataloged yet.
      </p>
      <p className="mt-2 text-xs text-slate-500 max-w-md mx-auto">
        Entries are automatically extracted from interviews and documents as they're processed. Once the collective starts sharing knowledge, it'll appear here.
      </p>
    </div>
  )
}

// Mobile-only slide-over drawer that reuses FacetPanel in its "drawer"
// variant. Hidden on lg screens where the sidebar is always visible.
function MobileFacetDrawer({ open, onClose }) {
  if (!open) return null
  return (
    <div className="lg:hidden fixed inset-0 z-50 flex" role="dialog" aria-modal="true" aria-label="Catalog filters">
      <div
        className="fixed inset-0 bg-slate-900/40"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="relative ml-auto flex h-full w-80 max-w-full flex-col bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <h3 className="text-sm font-semibold text-slate-900">Filters</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close filters"
            className="text-slate-400 hover:text-slate-600"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          <FacetPanel variant="drawer" />
        </div>
      </div>
    </div>
  )
}

function ErrorState({ message, onRetry }) {
  return (
    <div className="rounded-xl border border-rose-200 bg-rose-50 p-6">
      <p className="text-sm font-medium text-rose-800">Couldn't load the catalog</p>
      {message && <p className="mt-1 text-xs text-rose-600">{message}</p>}
      <button
        type="button"
        onClick={onRetry}
        className="mt-4 rounded-md bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-500"
      >
        Retry
      </button>
    </div>
  )
}

// ---- Main page ----------------------------------------------------------
export default function CatalogBrowser() {
  const [searchParams, setSearchParams] = useSearchParams()

  const entries         = useCatalogStore((s) => s.entries)
  const totalCount      = useCatalogStore((s) => s.totalCount)
  const totalPages      = useCatalogStore((s) => s.totalPages)
  const loading         = useCatalogStore((s) => s.loading)
  const error           = useCatalogStore((s) => s.error)
  const filters         = useCatalogStore((s) => s.filters)
  const sort            = useCatalogStore((s) => s.sort)
  const status          = useCatalogStore((s) => s.status)
  const page            = useCatalogStore((s) => s.page)
  const per_page        = useCatalogStore((s) => s.per_page)
  const setSort         = useCatalogStore((s) => s.setSort)
  const setStatus       = useCatalogStore((s) => s.setStatus)
  const setPage         = useCatalogStore((s) => s.setPage)
  const clearAllFilters = useCatalogStore((s) => s.clearAllFilters)
  const fetchEntries    = useCatalogStore((s) => s.fetchEntries)

  const hydratedRef = useRef(false)
  // The sync-back (store → URL) effect must skip its mount-commit run: at
  // that point its render closure still holds PRE-hydration store values, and
  // writing them out would clobber deep-link params (e.g. arriving from
  // Intersections' "View in catalog" links).
  const syncBackReadyRef = useRef(false)
  // Canonical form of the last URL we wrote ourselves, so the re-hydrate
  // effect can tell our own write-backs apart from real navigation.
  const lastWrittenUrlRef = useRef(null)
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false)

  const applyUrlToStore = useCallback(() => {
    const parsed = readFiltersFromUrl(searchParams)
    useCatalogStore.setState({
      filters:  parsed.filters,
      sort:     parsed.sort,
      status:   parsed.status,
      page:     parsed.page,
      per_page: parsed.per_page,
    })
    fetchEntries()
  }, [searchParams, fetchEntries])

  // Serialize the CURRENT store state (not render-closure values, which can
  // lag behind because applyUrlToStore writes the store directly).
  const currentStoreUrl = () => {
    const s = useCatalogStore.getState()
    return writeFiltersToUrl({
      filters:  s.filters,
      sort:     s.sort,
      status:   s.status,
      page:     s.page,
      per_page: s.per_page,
    })
  }

  // One-time hydration from URL on mount.
  useEffect(() => {
    if (hydratedRef.current) return
    hydratedRef.current = true
    applyUrlToStore()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Re-hydrate when the URL changes from OUTSIDE the store — e.g. following a
  // deep-link (/catalog?initiative_id=…&search=…) while already mounted, or
  // browser back/forward. Our own write-backs are recognized via
  // lastWrittenUrlRef (exact match) or by serializing the live store state.
  useEffect(() => {
    if (!hydratedRef.current) return

    const incoming = writeFiltersToUrl(readFiltersFromUrl(searchParams)).toString()
    if (incoming === lastWrittenUrlRef.current) return
    if (incoming === currentStoreUrl().toString()) return

    applyUrlToStore()
    // Only react to URL changes here; store-driven changes flow through the
    // sync-back effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  // After hydration, sync store → URL whenever filter state changes.
  useEffect(() => {
    if (!syncBackReadyRef.current) {
      // Mount-commit run: closure holds pre-hydration values — skip (FE-2).
      syncBackReadyRef.current = true
      return
    }
    const next = currentStoreUrl()
    lastWrittenUrlRef.current = next.toString()
    setSearchParams(next, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, sort, status, page, per_page, setSearchParams])

  const handleSortChange = (e) => {
    setSort(e.target.value)
    fetchEntries()
  }

  const handleStatusChange = (e) => {
    setStatus(e.target.value)
    fetchEntries()
  }

  const handlePageChange = useCallback((nextPage) => {
    setPage(nextPage)
    fetchEntries()
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [setPage, fetchEntries])

  const handleClearAll = () => {
    clearAllFilters()
    fetchEntries()
  }

  const hasAnyFilter = useMemo(() => (
    Object.values(filters.dimensions || {}).some((v) => v && v.length > 0) ||
    filters.entry_type ||
    (filters.search && filters.search.length > 0) ||
    filters.initiative_id ||
    filters.person_id
  ), [filters])

  const resultSummary = useMemo(() => {
    if (loading && entries.length === 0) return 'Loading…'
    const noun = totalCount === 1 ? 'entry' : 'entries'
    if (filters.search && filters.search.length > 0) {
      return `${totalCount} ${noun} matching "${filters.search}"`
    }
    return `${totalCount} ${noun}`
  }, [loading, entries.length, totalCount, filters.search])

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight text-slate-900">Catalog</h2>
        <p className="mt-1 text-sm text-slate-500">
          Distilled knowledge from interviews and documents — browse by dimension, search, and filter.
        </p>
      </div>

      <CatalogNav />

      <div className="flex gap-6">
        <FacetPanel />

        <main className="flex-1 min-w-0 space-y-5">
          <CatalogSearch />
          <EntryTypeFilter />

          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setMobileFiltersOpen(true)}
                className="lg:hidden rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
                aria-label="Show filters"
              >
                ⚙ Filters
              </button>
              <div className="text-sm text-slate-600" aria-live="polite">{resultSummary}</div>
            </div>
            <div className="flex items-center gap-2">
              <label htmlFor="catalog-status" className="text-xs text-slate-500">Status</label>
              <select
                id="catalog-status"
                value={status}
                onChange={handleStatusChange}
                className="rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm text-slate-700 focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500"
              >
                <option value="published">Published</option>
                <option value="draft">Drafts (review queue)</option>
                <option value="all">All</option>
              </select>

              <label htmlFor="catalog-sort" className="ml-1 text-xs text-slate-500">Sort</label>
              <select
                id="catalog-sort"
                value={sort}
                onChange={handleSortChange}
                className="rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm text-slate-700 focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500"
              >
                <option value="created_at">Newest</option>
                <option value="title">A–Z</option>
                <option value="relevance">Relevance</option>
              </select>
            </div>
          </div>

          {loading && entries.length === 0 && <SkeletonGrid count={6} />}

          {error && !loading && (
            <ErrorState message={error} onRetry={() => fetchEntries()} />
          )}

          {!loading && !error && entries.length === 0 && (
            hasAnyFilter
              ? <FilteredEmptyState onClear={handleClearAll} />
              : <FirstRunEmptyState />
          )}

          {entries.length > 0 && (
            <div className={`grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3 ${loading ? 'opacity-50' : ''}`}>
              {entries.map((entry) => (
                <CatalogEntryCard key={entry.id} entry={entry} />
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <nav className="flex items-center justify-between pt-4">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => handlePageChange(page - 1)}
                className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                ← Previous
              </button>
              <span className="text-xs text-slate-500">
                Page {page} of {totalPages}
              </span>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => handlePageChange(page + 1)}
                className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next →
              </button>
            </nav>
          )}
        </main>
      </div>

      <MobileFacetDrawer
        open={mobileFiltersOpen}
        onClose={() => setMobileFiltersOpen(false)}
      />
    </div>
  )
}
